import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsMobile } from '../utils/useIsMobile';
import { Link, useSearchParams } from '../routing';
import { catalogApi, classificationsApi, suppliersApi, formatCurrency } from '../utils/api';
import { toast } from '../components/Toast';
import SearchableSelect from '../components/SearchableSelect';
import Icon from '../components/Icon';
import ExpandCaret from '../components/ExpandCaret';
import PresetClassificationPicker from '../components/PresetClassificationPicker';
import ClassificationSheet from '../components/ClassificationSheet';
import ClassificationRulesCard from '../components/ClassificationRulesCard';
import UntaggedShortcut, { UNTAGGED } from '../components/UntaggedShortcut';
import CoverageBand, { coverageStatus } from '../components/CoverageBand';
import { classificationColor, nextClassificationColor } from '../utils/classificationColors';
import { useAuth } from '../context/AuthContext';
import PageViewAsMenu from '../components/ViewAsMenu';

// סיווג פריטים — one screen for defining classifications and tagging every
// product across every supplier.
//
// NOT "ניהול קטלוג": "קטלוג" already means editing a product's name/SKU/price
// elsewhere in this app, and a user hunting for that would land here and not
// find it.
//
// The classification list on the right IS the filter — clicking one filters the table
// on the left. Right, not left, because in RTL that is where the eye lands
// first, so it reads as primary navigation rather than a sidebar.

// Above this share of spend, offering the preset list as a visible option is
// actively harmful: creating empty classifications is the cheaper-looking action but
// moves no number, so a user's first click appears to do nothing.
const PRESETS_HIDDEN_ABOVE_PCT = 60;

// How many catalog rows one request brings back. This used to be a bare
// `limit: 500` with no `offset`, which silently truncated: the server returns a
// real `total`, so the page printed "1199 מוצרים" above a table that could only
// ever show 500 — and since the ORDER BY puts untagged first, a tenant with more
// than 500 untagged items had rows that could NEVER be categorised on the one
// screen that categorises them. The server has always supported offset paging
// (and orders by a unique `pc.id` tiebreaker precisely so pages don't skip or
// repeat); only the client was missing.
const PAGE_SIZE = 500;

// Mirrors the hard ceiling in GET /api/catalog/products (`Math.min(…, 2000)`).
// Only used when restoring an already-loaded list after a mutation; if a user
// had somehow loaded more than this, the restore stops here and the "טען עוד"
// button simply reappears — visibly short, never silently truncated.
const MAX_SERVER_LIMIT = 2000;

// The suggestion review table, shared by the first-run card and the maintenance
// view. Apply sits at the TOP: with dozens of suppliers it would otherwise be
// below the fold, past every row.
// This screen is reached FROM ניתוח רכש — it is not a nav item — so without a
// way back the only exit is the browser button. Present on every render path,
// including the error and first-run ones, which are exactly the states a user
// most wants to leave.
function CatalogBreadcrumb() {
  return (
    <div className="breadcrumb">
      <Link className="link" to="/analysis">ניתוח רכש</Link>
      <Icon name="chevron-inline" size={13} className="breadcrumb-sep" />
      <span>סיווג פריטים</span>
    </div>
  );
}

// The column names its basis on the headline and its window underneath.
//
// The basis is also in the band's page-wide footnote, and it is repeated here
// on purpose: the reader meets these numbers mid-table with the band scrolled
// away, and "is this with VAT?" is the question that decides whether a figure
// can be compared to anything else they know.
function SpendHeader({ days }) {
  return (
    <th style={{ whiteSpace: 'nowrap' }}>
      רכש (ללא מע״מ)
      <span style={{ display: 'block', fontWeight: 400, fontSize: 11.5, color: '#94a3b8' }}>
        {days} יום אחרונים
      </span>
    </th>
  );
}

function SuggestionTable({ sugg, picked, setPicked, pickedCount, busy, onApply, compact }) {
  const rows = sugg?.suggestions || [];
  const days = sugg?.window?.days;
  return (
    <>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        {/* Disabled rather than erroring on click: "apply 0" is not a thing a
            user should be able to ask for. */}
        <button className="btn btn-primary" disabled={busy || pickedCount === 0} onClick={onApply}>
          החל {pickedCount} הצעות
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => setPicked({})}>נקה בחירה</button>
        <button className="btn btn-secondary btn-sm"
          onClick={() => setPicked(Object.fromEntries(rows.map(s => [s.supplier_id, true])))}>
          בחר הכל
        </button>
      </div>
      <div className="table-wrapper table-scroll-mobile">
        <table>
          <thead>
            <tr>
              <th style={{ width: 34 }}></th>
              <th>ספק</th><th>סיווג מוצעת</th><th>ראיה</th>
              {!compact && <th>לא משויכים</th>}
              <SpendHeader days={days} />
            </tr>
          </thead>
          <tbody>
            {rows.map(s => (
              <tr key={s.supplier_id} style={{ background: picked[s.supplier_id] ? '#f2f8f5' : undefined }}>
                <td>
                  <input type="checkbox" checked={!!picked[s.supplier_id]}
                    onChange={e => setPicked(p => ({ ...p, [s.supplier_id]: e.target.checked }))} />
                </td>
                <td style={{ fontWeight: 500 }}>{s.supplier_name}</td>
                <td>{s.canonical_name}</td>
                {/* The evidence column is the trust mechanism — never hidden,
                    at any breakpoint. Without it these are unexplained guesses.
                    It credits PEAK, not "other restaurants". The mechanism is
                    unchanged — the vote really does come from other tenants —
                    but to this reader those are strangers' businesses, not
                    branches of their own, and naming them invited exactly the
                    wrong question ("which restaurants? who can see mine?").
                    Peak is the honest actor: the platform is what knows this.
                    Keep the COUNT: it is what separates a pre-ticked suggestion
                    from one waiting on them, and dropping it would turn the
                    evidence back into a guess. */}
                <td style={{ fontSize: 12.5, color: 'var(--ink-muted)' }}>
                  {s.confidence === 'high'
                    ? `מבוסס על ${s.evidence.restaurants} מקורות ב-Peak`
                    : s.confidence === 'medium'
                      ? 'מקור אחד בלבד — לאישורך'
                      : `אין הסכמה: ${s.evidence.alternatives.map(a => a.canonical_name).join(' / ')}`}
                </td>
                {!compact && <td>{s.untagged_products}</td>}
                <td style={{ whiteSpace: 'nowrap' }}>{formatCurrency(s.spend)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// First run — a restaurant with no classifications at all. Five live restaurants are
// in this state holding ~₪3.3M of untagged spend, so this is the highest-value
// moment in the feature. It gets ONE primary action.
function FirstRun({
  sugg, canEdit, busy, picked, setPicked, pickedCount,
  showSuggestions, setShowSuggestions, applyPicked,
  createPresets,
}) {
  const count = (sugg?.suggestions || []).length;
  const pct = sugg?.suggested_spend_pct ?? 0;
  // When suggestions already cover most of the spend, the preset list is a trap
  // rather than an alternative — demote it to a plain link.
  const presetsAsLink = pct >= PRESETS_HIDDEN_ABOVE_PCT;
  const [showPresets, setShowPresets] = useState(false);

  if (!canEdit) {
    return (
      <div className="empty-state">
        <div className="empty-icon"><Icon name="tag" size={34} strokeWidth={1.6} /></div>
        <p>עדיין לא הוגדרו סיווגים במסעדה. לסיווג מוצרים פנה למנהל.</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ borderInlineStart: '3px solid #2f5c8f' }}>
      {/* Heading only (owner). The "why classify at all" subtitle came out: the
          person on this screen already opened it to classify, so the card sells
          nothing and goes straight to the offer. */}
      <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>עדיין לא סיווגת את הרכש</h2>

      {count > 0 ? (
        <div style={{ background: '#f7faf8', border: '1px solid #cfe3d8', borderRadius: 10, padding: 16 }}>
          {/* Phrased as an OFFER, not a fact about other tenants (owner). "כבר
              מסווגים ב-Peak" described a state and left the reader to work out
              what it had to do with them; this names the actor and the action
              they are being offered. */}
          <div style={{ fontSize: 15, marginBottom: 4 }}>
            Peak רוצה להציע סיווג אוטומטי ל־<strong>{count} מהספקים שלך</strong>.
          </div>
          {sugg.suggested_spend > 0 && (
            <div style={{ color: 'var(--ink-muted)', fontSize: 13.5 }}>
              זה מכסה {formatCurrency(sugg.suggested_spend)} — {pct}% מהרכש ב-{sugg.window.days} הימים האחרונים.
            </div>
          )}

          <div style={{ margin: '14px 0' }}>
            {/* The label carries the count that will actually be applied, so
                nothing about the outcome is a surprise. When nothing is
                pre-ticked (no supplier has 2+ agreeing sources) "apply 0"
                is meaningless — the primary becomes REVIEW instead, and opens
                the table, because choosing is then the real first step. */}
            {pickedCount > 0 ? (
              <button className="btn btn-primary" disabled={busy}
                onClick={applyPicked} style={{ fontSize: 15, padding: '10px 18px' }}>
                סווג אוטומטית {pickedCount} ספקים
              </button>
            ) : (
              <button className="btn btn-primary" disabled={busy}
                onClick={() => setShowSuggestions(true)} style={{ fontSize: 15, padding: '10px 18px' }}>
                סקור {count} הצעות ובחר
              </button>
            )}
          </div>

          {/* No grey footnote here (owner). It carried two things: the
              preselect/pending split, and "ההחלה יוצרת את הסיווגים בעצמה".
              Both still exist where they are actionable — the per-row evidence
              column behind "הצג את כל N ההצעות" says what each suggestion rests
              on, and the button label names how many will actually be applied. */}

          <button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }}
            onClick={() => setShowSuggestions(v => !v)}>
            <ExpandCaret open={showSuggestions} muted={false} />
            {showSuggestions ? ' הסתר' : ` הצג את כל ${count} ההצעות לפני החלה`}
          </button>

          {showSuggestions && (
            <div style={{ marginTop: 12 }}>
              <SuggestionTable sugg={sugg} picked={picked} setPicked={setPicked}
                pickedCount={pickedCount} busy={busy} onApply={applyPicked} />
            </div>
          )}
        </div>
      ) : (
        <div style={{ color: 'var(--ink-muted)', fontSize: 13.5, marginBottom: 14 }}>
          לא מצאנו ספקים מוכרים שאפשר לסווג אוטומטית — נתחיל מרשימת סיווגים מוכנה.
        </div>
      )}

      {/* Presets. A link when suggestions already carry the day, the primary
          action when there is nothing else to offer. Same component the
          management sheet renders — this card is no longer the only way in. */}
      <div style={{ marginTop: count > 0 ? 16 : 0 }}>
        {presetsAsLink && !showPresets ? (
          <button className="btn btn-secondary btn-sm" onClick={() => setShowPresets(true)}>
            אין ספקים מתאימים? התחל מרשימת סיווגים מוכנה
          </button>
        ) : (
          /* existing={[]} — first run means nothing exists yet, so offer all of them. */
          <PresetClassificationPicker
            presets={sugg?.presets || []}
            existing={[]}
            onCreate={createPresets}
            busy={busy}
            variant="inline"
            primary={count === 0}
          />
        )}
      </div>
    </div>
  );
}

export default function ItemClassificationPage() {
  // Table vs cards switches with the filter bar and the view tabs, at the
  // filter-system breakpoint (720) — not at 640, which is the DENSITY step.
  const isMobile = useIsMobile();
  const { canEdit } = useAuth();
  // Arriving from the "לא מסווג" call-to-action on ניתוח רכש: the user came
  // here with an intent, so honour it instead of dropping them on "all".
  const [searchParams] = useSearchParams();
  const arrivedForUntagged = searchParams.get('untagged') === '1';

  const [coverage, setCoverage]   = useState(null);
  const [classifications, setClassifications] = useState([]);
  const [products, setProducts]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [sugg, setSugg]           = useState(null);

  const [loading, setLoading]     = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  // Separate from `loadingProducts` on purpose: that one swaps the whole table
  // for a spinner, which would make the rows you are reading vanish mid-scroll
  // every time you asked for more of them.
  const [loadingMore, setLoadingMore] = useState(false);
  const [busy, setBusy]           = useState(false);

  // Identifies the filter/search combination a response belongs to, so a late
  // page cannot append itself to a list it was never queried for.
  const queryKeyRef = useRef('');

  // filters
  const [coverageOpen, setCoverageOpen] = useState(true);
  const [activeCat, setActiveCat] = useState(arrivedForUntagged ? UNTAGGED : null);   // classification id | UNTAGGED | null
  const [supplierId, setSupplierId] = useState('');
  const [q, setQ]                 = useState('');

  const [selected, setSelected]   = useState([]);
  const [bulkPickerKey, setBulkPickerKey] = useState(0);   // remounts the bulk combobox to clear it
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [justApplied, setJustApplied] = useState(null);   // success moment after an apply
  const [picked, setPicked]       = useState({});     // suggestion supplier_id → bool

  // Errors are surfaced, not swallowed. On THIS screen a silent failure erases
  // the entire feature: with `sugg` null and `classifications` empty, a first-run
  // user would see a title and an empty table and conclude it is broken.
  const [loadError, setLoadError] = useState('');

  // Item-type classification management. Budgets are NOT here any more: they belong
  // to קטגוריות on /budget, which is a different question asked by a
  // different person at a different time. This screen is tagging.
  //
  // Inline and collapsed by default (see ClassificationSheet), matching the budget
  // lines card on /budget: managing classifications is occasional setup done while
  // looking at the tagging table, so it sits on the page rather than behind a
  // button and an overlay.
  const [catSheetOpen, setCatSheetOpen] = useState(false);

  // Classification automations (supplier → default classification). Collapsed by
  // default like the classification sheet. Needs the FULL supplier list (not just
  // suppliers on the current product page), so it is fetched here once.
  const [rulesOpen, setRulesOpen] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState([]);

  // First-ever landing with nothing to auto-suggest (no recognised suppliers):
  // skip the FirstRun card's preset grid and open straight into ניהול סיווג
  // פריטים instead, with the tagging card collapsed — there is nothing to tag
  // yet either way. Runs once, right after the initial load settles.
  const autoOpenedRef = useRef(false);

  const loadCoverage   = useCallback(() => catalogApi.coverage()
    .then(r => setCoverage(r.data))
    .catch(e => setLoadError(e?.response?.data?.error || 'שגיאה בטעינת נתוני הכיסוי')), []);
  const loadClassifications = useCallback(() => classificationsApi.getAll()
    .then(r => setClassifications(r.data))
    .catch(e => setLoadError(e?.response?.data?.error || 'שגיאה בטעינת הסיווגים')), []);

  const loadSuggestions = useCallback(() => (
    catalogApi.suggestions()
      .then(r => {
        setSugg(r.data);
        // Only the tier that backtests ~95% arrives pre-ticked; everything else
        // the user has to agree to.
        const pre = {};
        (r.data.suggestions || []).forEach(s => { if (s.preselect) pre[s.supplier_id] = true; });
        setPicked(pre);
      })
      .catch(e => setLoadError(e?.response?.data?.error || 'שגיאה בטעינת ההצעות'))
  ), []);

  // `offset > 0` APPENDS (the "טען עוד" path); offset 0 replaces. Filters and
  // search are sent to the server, never applied to the rows already in hand —
  // that distinction is the whole point of paging here. Filtering client-side
  // would search only the loaded page and quietly report "no results" for items
  // that exist, which is worse than the truncation it would be hiding.
  const loadProducts = useCallback((offset = 0, size = PAGE_SIZE) => {
    const append = offset > 0;
    if (append) setLoadingMore(true); else setLoadingProducts(true);

    const params = { limit: size, offset };
    if (supplierId) params.supplier_id = supplierId;
    if (activeCat === UNTAGGED) params.untagged = 1;
    else if (activeCat) params.classification_id = activeCat;
    if (q.trim()) params.q = q.trim();

    // A page-2 request in flight when the filters change would otherwise append
    // rows belonging to the PREVIOUS query onto the new list. The key is
    // captured here and re-checked on arrival; a stale response is dropped.
    const key = `${supplierId}|${String(activeCat)}|${q.trim()}`;
    queryKeyRef.current = key;

    return catalogApi.products(params)
      .then(r => {
        if (queryKeyRef.current !== key) return;
        const rows = r.data.products || [];
        setProducts(prev => (append ? [...prev, ...rows] : rows));
        setTotal(r.data.total || 0);
      })
      .catch(() => toast('שגיאה בטעינת המוצרים', 'error'))
      .finally(() => { if (append) setLoadingMore(false); else setLoadingProducts(false); });
  }, [supplierId, activeCat, q]);

  useEffect(() => {
    Promise.all([
      loadCoverage(), loadClassifications(), loadSuggestions(),
    ]).finally(() => setLoading(false));
  }, [loadCoverage, loadClassifications, loadSuggestions]);

  useEffect(() => {
    if (loading || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    const noSuggestions = (sugg?.suggestions || []).length === 0;
    if (canEdit && classifications.length === 0 && noSuggestions) {
      setCatSheetOpen(true);
      setCoverageOpen(false);
    }
  }, [loading, classifications, sugg, canEdit]);

  // Full supplier list — include_empty so a supplier with no docs yet can still
  // get a classification rule. NOT gated on canEdit any more: the supplier
  // FILTER above the table is built from this too, and a viewer needs to filter
  // just as much as an editor. `GET /api/suppliers` only requires auth, so this
  // grants a viewer nothing they could not already read.
  // The handful of suppliers with no catalog items (0-6 per tenant here) are
  // accepted noise in the filter — far cheaper than omitting real ones.
  useEffect(() => {
    suppliersApi.getAll({ include_empty: true })
      .then(r => setAllSuppliers(r.data || []))
      .catch(() => setAllSuppliers([]));
  }, []);

  // Debounced so typing in the search box doesn't fire a query per keystroke —
  // this endpoint aggregates spend and the server is synchronous.
  useEffect(() => {
    // Explicit 0: `setTimeout(loadProducts, …)` would hand the callback the
    // timer's own argument, which now means an offset.
    const t = setTimeout(() => loadProducts(0), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadProducts, q]);

  // Changing a filter drops the selection. Without this the ids survive into a
  // result set that no longer contains them, and the sticky bulk bar keeps
  // offering to assign a classification to rows the user can no longer see — "שויכו 40
  // מוצרים" for forty rows that are not on screen and were chosen under a
  // different question. Bulk edits must only ever act on what is visible.
  //
  // Deliberately keyed on the FILTERS and not on `products`: the selection is
  // meant to survive "טען עוד", which also replaces `products`. Clearing there
  // would break paging through a long catalog while tagging as you go, which is
  // the whole reason the selection persists across loads.
  useEffect(() => { setSelected([]); }, [supplierId, activeCat, q]);

  // Refresh after a mutation WITHOUT collapsing back to the first page. Calling
  // loadProducts() bare would reset a 1199-row list to 500 and throw away every
  // page the user had asked for — on the one screen whose entire workflow is
  // "load more, tag a batch, repeat". Re-requests as many rows as were on
  // screen, in one call, so the list re-sorts (tagging moves rows to the back)
  // without shrinking under the user.
  const reloadProducts = useCallback(() => (
    loadProducts(0, Math.min(Math.max(products.length, PAGE_SIZE), MAX_SERVER_LIMIT))
  ), [loadProducts, products.length]);

  // Derived from what is loaded vs the server's real COUNT(*) for the current
  // filters — never from whether the last page came back full, which would need
  // one extra empty request to discover the end.
  const hasMore = products.length < total;

  // Built from the FULL supplier list, not from the loaded rows. Deriving it
  // from `products` meant the dropdown only ever offered suppliers that happened
  // to appear in the current page: on the largest tenant that was 91 of 133, and
  // the 42 it omitted were exactly the ones whose items sat past the cap — so
  // the obvious workaround for the truncation (filter to one supplier) was
  // unavailable for precisely the suppliers that needed it.
  const suppliers = useMemo(() => (
    allSuppliers
      .map(s => [s.id, s.name])
      .sort((a, b) => (a[1] || '').localeCompare(b[1] || '', 'he'))
  ), [allSuppliers]);

  // Option lists for the shared SearchableSelect combobox.
  const supplierOptions = useMemo(
    () => [{ value: '', label: 'כל הספקים' }, ...suppliers.map(([id, name]) => ({ value: id, label: name }))],
    [suppliers],
  );
  // Each option carries its classification's swatch, so the table, the chips, the
  // budget sheet and the pie all identify a classification by the same colour and a
  // row can be placed without reading the name.
  const colouredCats = useMemo(
    () => classifications.map((c, i) => ({ value: c.id, label: c.name, color: classificationColor(c, i) })),
    [classifications],
  );
  // Supplier picker options for the classification-rules card.
  const ruleSupplierOptions = useMemo(
    () => allSuppliers.map(s => ({ value: s.id, label: s.name })),
    [allSuppliers],
  );
  const classificationOptions = useMemo(
    () => [{ value: '', label: '— ללא —' }, ...colouredCats], [colouredCats],
  );
  const bulkOptions = useMemo(
    () => [{ value: 'none', label: '— הסר סיווג —' }, ...colouredCats], [colouredCats],
  );
  // ללא סיווג is IN this list, not only on the shortcut chip above: with the
  // chip pressed the dropdown would otherwise read "כל הסיווגים" over a table
  // showing one bucket. Two controls, one piece of state, always agreeing.
  const classificationFilterOptions = useMemo(() => [
    { value: '', label: 'כל הסיווגים' },
    { value: UNTAGGED, label: 'ללא סיווג' },
    ...colouredCats,
  ], [colouredCats]);

  // ── mutations ─────────────────────────────────────────────────────────────

  const assign = async (product, classificationId) => {
    const prev = product.classification_id;
    // Optimistic, with rollback: a failed PATCH must not leave a green pill and
    // an inflated coverage number behind.
    setProducts(list => list.map(p => (p.id === product.id ? { ...p, classification_id: classificationId } : p)));
    try {
      await catalogApi.setItemClassification(product.id, classificationId);
      loadCoverage(); loadClassifications();
    } catch {
      setProducts(list => list.map(p => (p.id === product.id ? { ...p, classification_id: prev } : p)));
      toast('שגיאה בשמירת הסיווג', 'error');
    }
  };

  // The one assignment control, rendered by BOTH the desktop table cell and
  // the phone card. Two copies would be two places for the create-and-assign
  // path to drift, and that path is the whole reason a missing classification never
  // dead-ends a row.
  const classificationControl = (p) => {
    if (!canEdit) {
      return (
        <span style={{ color: p.classification_name ? undefined : '#94a3b8' }}>
          {p.classification_name || '— ללא —'}
        </span>
      );
    }
    return (
      // The shared combobox, like the rest of the app: type to filter, with
      // "create" folded into the list so a missing classification is created AND
      // assigned in one gesture.
      <SearchableSelect
        value={p.classification_id ?? ''}
        options={classificationOptions}
        placeholder="— ללא —"
        ariaLabel={`סיווג עבור ${p.name || 'מוצר'}`}
        onChange={v => assign(p, v === '' ? null : parseInt(v))}
        onCreate={async (name) => {
          try {
            const r = await classificationsApi.create({ name, color: nextClassificationColor(classifications) });
            await loadClassifications();
            await assign(p, r.data.id);
          } catch (e) {
            toast(e?.response?.data?.error || 'שגיאה ביצירת הסיווג', 'error');
          }
        }}
        createLabel={q => `צור סיווג "${q}" ושייך`}
      />
    );
  };

  const bulkAssign = async (classificationId) => {
    if (selected.length === 0) return;
    setBusy(true);
    try {
      await catalogApi.bulkSetClassification(selected, classificationId);
      toast(`שויכו ${selected.length} מוצרים`);
      setSelected([]);
      await Promise.all([reloadProducts(), loadCoverage(), loadClassifications()]);
    } catch {
      toast('שגיאה בשיוך המוני', 'error');
    } finally { setBusy(false); }
  };

  const applyPicked = async () => {
    const apply = (sugg?.suggestions || [])
      .filter(s => picked[s.supplier_id])
      .map(s => ({ supplier_id: s.supplier_id, canonical_id: s.canonical_id }));
    if (apply.length === 0) return;   // the button is disabled in this state
    setBusy(true);
    try {
      const r = await catalogApi.applySuggestions(apply);
      setShowSuggestions(false);
      // Held in state rather than fired as a toast: this is where a first-run
      // user learns what the screen is FOR, and a toast disappears before they
      // have connected the action to the number moving.
      setJustApplied({ ...r.data, from: coverage?.spend?.pct_tagged ?? 0 });
      await Promise.all([reloadProducts(), loadCoverage(), loadClassifications(), loadSuggestions()]);
    } catch (e) {
      toast(e?.response?.data?.error || 'שגיאה בהחלת ההצעות', 'error');
    } finally { setBusy(false); }
  };

  // Takes the chosen canonical ids: selection state lives in
  // PresetClassificationPicker now, because the picker renders in two places
  // (first run and the management sheet) and a single page-level `presetPick`
  // would have made one instance's ticks appear in the other.
  const createPresets = async (canonicalIds = []) => {
    const wanted = new Set(canonicalIds);
    const names = (sugg?.presets || []).filter(p => wanted.has(p.canonical_id)).map(p => p.name);
    if (names.length === 0) { toast('לא נבחרו סיווגים', 'error'); return; }
    setBusy(true);
    try {
      // Colour as we go, not all at once: each create has to see the ones
      // before it or the whole batch picks the same "least used" entry.
      const created = [...classifications];
      for (const name of names) {
        const color = nextClassificationColor(created);
        await classificationsApi.create({ name, color });
        created.push({ color });
      }
      toast(`נוצרו ${names.length} סיווגים`);
      // loadSuggestions too: the picker filters on what already exists, so
      // without this the rows it just created stay on offer until a reload.
      await Promise.all([loadClassifications(), loadCoverage(), loadSuggestions()]);
    } catch {
      toast('שגיאה ביצירת הסיווגים', 'error');
    } finally { setBusy(false); }
  };

  // ── render ────────────────────────────────────────────────────────────────

  if (loading) return <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>;

  // Admin with no restaurant selected: all three endpoints 400 by design, so
  // without this the screen renders as an empty shell with no explanation.
  if (loadError) {
    return (
      <div>
        <CatalogBreadcrumb />
        <h1 className="page-title">ניהול סיווג פריטים</h1>
        <div className="empty-state">
          <div className="empty-icon"><Icon name="store" size={34} strokeWidth={1.6} /></div>
          <p>{loadError}</p>
        </div>
      </div>
    );
  }

  const cov = coverage?.spend;
  const hasClassifications = classifications.length > 0;
  const openSuggestions = (sugg?.suggestions || []).length;
  const pickedCount = Object.values(picked).filter(Boolean).length;

  // ── First run: no classifications yet ──────────────────────────────────────────
  // Deliberately NOT the management screen — UNLESS there is nothing to
  // suggest. With real supplier suggestions on offer this card is the
  // highest-value moment (see FirstRun above) and earns its own screen. But
  // with zero suggestions its only content is "start from the preset list",
  // which is just ClassificationSheet's own preset picker wearing a second
  // costume — so that case falls through to the normal page instead, landed
  // with ניהול סיווג פריטים open (see the effect above) instead of showing
  // both.
  if (!hasClassifications && !justApplied && (!canEdit || openSuggestions > 0)) {
    return (
      <div>
        <CatalogBreadcrumb />
        <h1 className="page-title">ניהול סיווג פריטים</h1>
        <FirstRun
          sugg={sugg}
          canEdit={canEdit}
          busy={busy}
          picked={picked}
          setPicked={setPicked}
          pickedCount={pickedCount}
          showSuggestions={showSuggestions}
          setShowSuggestions={setShowSuggestions}
          applyPicked={applyPicked}
          createPresets={createPresets}
        />
      </div>
    );
  }

  const untaggedSpend = cov ? cov.untagged : 0;


  return (
    <div>
      <CatalogBreadcrumb />
      <div className="page-title-row">
        <h1 className="page-title">ניהול סיווג פריטים</h1>
        <PageViewAsMenu />
      </div>

      {/* The success moment. A first-run user learns what this screen is for
          from seeing the number move — a toast vanishes too fast to connect
          the action to the result. */}
      {justApplied && (
        <div className="card" style={{ marginBottom: 16, borderInlineStart: '3px solid #2c7d58', background: '#f2f8f5' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="check" size={18} style={{ color: '#2c7d58' }} />
                סיווגנו {justApplied.tagged_products} מוצרים
                {justApplied.created_classifications > 0 && ` · נוצרו ${justApplied.created_classifications} סיווגים`}
              </div>
              {cov && (
                <div style={{ color: '#2c7d58', fontSize: 13.5, marginTop: 3 }}>
                  הכיסוי עלה מ-{justApplied.from}% ל-{cov.pct_tagged}% מההוצאה
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Link className="btn btn-secondary btn-sm" to="/analysis">חזרה לניתוח רכש</Link>
              <button className="btn btn-secondary btn-sm" onClick={() => setJustApplied(null)}>המשך לסיווג</button>
            </div>
          </div>
        </div>
      )}

      {/* Classification management, inline and collapsed, ABOVE the coverage band —
          the setup you occasionally return to, kept on the page but folded away
          so it never competes with the progress meter and tagging table that
          are the daily work. Same collapsible-card idiom as /budget. */}
      {canEdit && (
        <ClassificationSheet
          classifications={classifications}
          // The five curated starters ONLY — deliberately not the whole
          // canonical vocabulary. That list ends in OPEX rows (כוח אדם,
          // שיווק, עמלות ובנקאות…) which are expense categories, not item
          // types, and offering 29 checkboxes on a screen about tagging
          // invoice lines is a worse answer than offering the 5 that fit.
          // Anything else is created by typing its name, which still
          // resolves to the canonical row when the name matches.
          presets={sugg?.presets || []}
          onCreatePresets={createPresets}
          open={catSheetOpen}
          onToggle={() => setCatSheetOpen(o => !o)}
          // Every edit applies immediately, so the sheet must refresh WITHOUT
          // collapsing — the user is still working in it.
          onRefresh={() => Promise.all([loadClassifications(), loadCoverage(), reloadProducts()])}
        />
      )}

      {/* Classification automations (supplier → default classification). Collapsed
          card in the same idiom, above the coverage band. Saving backfills the
          supplier's unclassified products, so refresh coverage + products. */}
      {canEdit && (
        <ClassificationRulesCard
          suppliers={ruleSupplierOptions}
          classificationOptions={colouredCats}
          open={rulesOpen}
          onToggle={() => setRulesOpen(o => !o)}
          onSaved={() => Promise.all([loadCoverage(), reloadProducts()])}
        />
      )}

      {/* One COLLAPSIBLE CARD (owner) — the same grammar as שיוך רכש
          לקטגוריות on /categories-management: the toggle row, the bar staying visible when
          collapsed (it is the one glanceable answer to "how far along am I"),
          and the body — now the filter bar and the product table too, so the
          whole working area is one continuous card rather than a coverage box
          sitting above a separate section.

          The card itself, and the products inside it, render UNCONDITIONALLY
          — never gated on `cov`. Coverage is its own async fetch and can lag
          or fail independently of the product list; before this card existed
          the table never depended on it, and folding the table into the card
          must not quietly make tagging products depend on a fetch that has
          nothing to do with tagging them. Only the coverage-specific pieces
          (the summary chip and the bar) check `cov` themselves. */}
      {(() => {
        const status = cov ? coverageStatus(cov) : null;
        return (
          <div className={`card category-lines-card cov-card${coverageOpen ? '' : ' is-collapsed'}`}>
            <button
              type="button" className="category-lines-toggle"
              onClick={() => setCoverageOpen(o => !o)}
              aria-expanded={coverageOpen} aria-controls="coverage-body"
            >
              <span className="category-lines-title">סיווג פריטים</span>
              {status && (
                <span className="category-lines-summary">
                  {status.done ? 'הושלם' : `${status.shownPct}% מסווג`}
                </span>
              )}
              <ExpandCaret open={coverageOpen} size={14} />
            </button>

            {cov && <CoverageBand spend={cov} open={coverageOpen} />}

            {coverageOpen && (
              <div id="coverage-body">
                {/* Directly under the bar, so progress and remaining work read
                    as one statement rather than a figure and an unrelated
                    filter. */}
                <UntaggedShortcut
                  amount={untaggedSpend}
                  active={activeCat === UNTAGGED}
                  onToggle={() => setActiveCat(activeCat === UNTAGGED ? null : UNTAGGED)}
                />

                <div>
                  {/* Products */}
                  <section style={{ minWidth: 0 }}>
                    <div className="filter-bar" style={{ marginBottom: 12 }}>
                      <div className="filter-group">
                        <label>חיפוש</label>
                        <input className="filter-input" placeholder="שם מוצר או מק״ט…" value={q} onChange={e => setQ(e.target.value)} style={{ width: 200 }} />
                      </div>
                      {/* Classification sits WITH the other two filters, in the same control,
                          instead of as a chip row in its own idiom 40px above them. The
                          chips carried per-classification ₪, which is analysis and belongs to
                          ניתוח רכש and the budget sheet — and keeping them usable at 17
                          classifications took a desktop cap, a mobile cap, an overflow
                          control, a promotion rule and an embedded search. */}
                      <div className="filter-group" style={{ minWidth: 210 }}>
                        {/* Same words as the column it filters, one row below. A filter
                            labelled differently from its column reads as a second,
                            unrelated control. */}
                        <label>סיווג פריט</label>
                        <SearchableSelect
                          value={activeCat ?? ''}
                          options={classificationFilterOptions}
                          placeholder="כל הסיווגים"
                          ariaLabel="סינון לפי סיווג פריט"
                          onChange={v => setActiveCat(v === '' ? null : (v === UNTAGGED ? UNTAGGED : Number(v)))}
                        />
                      </div>
                      <div className="filter-group" style={{ minWidth: 210 }}>
                        <label>ספק</label>
                        <SearchableSelect
                          value={supplierId}
                          options={supplierOptions}
                          placeholder="כל הספקים"
                          ariaLabel="סינון לפי ספק"
                          onChange={setSupplierId}
                        />
                      </div>
                      {(q || supplierId || activeCat) && (
                        <button className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                          onClick={() => { setQ(''); setSupplierId(''); setActiveCat(null); }}>
                          <Icon name="close" size={14} />
                          נקה סינון
                        </button>
                      )}
                      {/* While more rows remain, say so. Printing the bare total over a
                          truncated table was the original sin here: the page stated the
                          number of items it was not showing. */}
                      <span style={{ marginInlineStart: 'auto', color: 'var(--ink-muted)', fontSize: 13 }}>
                        {hasMore ? `${products.length} מתוך ${total} פריטים` : `${total} פריטים`}
                      </span>
                    </div>

                    {/* Sticky bulk bar — appears only with a selection. */}
                    {canEdit && selected.length > 0 && (
                      <div className="card" style={{ position: 'sticky', top: 8, zIndex: 5, marginBottom: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <strong>{selected.length} נבחרו</strong>
                        <div style={{ minWidth: 220 }}>
                          {/* Resets to empty after each apply (via bulkPickerKey) so the
                              control reads as an action, not a sticky selection. */}
                          <SearchableSelect
                            key={bulkPickerKey}
                            value=""
                            options={bulkOptions}
                            placeholder="שייך לסיווג…"
                            ariaLabel="שיוך המוני לסיווג"
                            onChange={v => {
                              if (!v || busy) return;
                              bulkAssign(v === 'none' ? null : parseInt(v));
                              setBulkPickerKey(k => k + 1);
                            }}
                          />
                        </div>
                        <button className="btn btn-secondary btn-sm" onClick={() => setSelected([])}>נקה בחירה</button>
                      </div>
                    )}

                    {loadingProducts ? (
                      <div className="loading-container"><div className="spinner" /></div>
                    ) : products.length === 0 ? (
                      <div className="empty-state">
                        {/* Two different empty states: everything IS tagged (a finished
                            job — the check), versus the filter simply matched nothing. */}
                        <div className="empty-icon">
                          <Icon name={activeCat === UNTAGGED ? 'check' : 'search'} size={34} strokeWidth={1.6} />
                        </div>
                        <p>{activeCat === UNTAGGED ? 'אין מוצרים ללא סיווג — הכול מסודר' : 'לא נמצאו מוצרים'}</p>
                      </div>
                    ) : (
                      <>
                      {/* Phone: a card list, NOT the desktop table with a sideways
                          scroll. In the table the spend column sits off-screen until
                          you swipe, and spend is how you decide which product is worth
                          tagging first — so the swipe hides the reason to act. */}
                      {isMobile && (
                      <div className="m-list">
                        {products.map(p => (
                          <div key={p.id} className={`prod-card${selected.includes(p.id) ? ' is-selected' : ''}`}>
                            <div className="prod-card-top">
                              {canEdit && (
                                <input type="checkbox" checked={selected.includes(p.id)}
                                  aria-label={`בחר ${p.name || 'מוצר'}`}
                                  onChange={e => setSelected(s => e.target.checked ? [...s, p.id] : s.filter(x => x !== p.id))} />
                              )}
                              <div className="prod-card-name">{p.name || '—'}</div>
                              {/* Prominent, and on its own end column so amounts line up
                                  down the list like a table column would. */}
                              <div className="prod-card-amount">{formatCurrency(p.spend)}</div>
                            </div>
                            <div className="prod-card-meta">
                              {p.supplier_name}
                              {p.sku ? <> · {p.sku}</> : null}
                            </div>
                            <div className="prod-card-cat">{classificationControl(p)}</div>
                          </div>
                        ))}
                      </div>
                      )}

                      {!isMobile && (
                      <div className="table-wrapper table-scroll-mobile">
                        <table>
                          <thead>
                            <tr>
                              {canEdit && (
                                <th style={{ width: 34 }}>
                                  <input type="checkbox"
                                    checked={selected.length > 0 && selected.length === products.length}
                                    ref={el => { if (el) el.indeterminate = selected.length > 0 && selected.length < products.length; }}
                                    onChange={e => setSelected(e.target.checked ? products.map(p => p.id) : [])} />
                                </th>
                              )}
                              <th>פריט</th><th>מק״ט</th><th>ספק</th><th>סיווג פריט</th>
                              <SpendHeader days={coverage?.window?.days} />
                            </tr>
                          </thead>
                          <tbody>
                            {products.map(p => (
                              <tr key={p.id}>
                                {canEdit && (
                                  <td>
                                    <input type="checkbox" checked={selected.includes(p.id)}
                                      onChange={e => setSelected(s => e.target.checked ? [...s, p.id] : s.filter(x => x !== p.id))} />
                                  </td>
                                )}
                                <td style={{ fontWeight: 500 }}>{p.name || '—'}</td>
                                <td style={{ color: '#94a3b8', fontSize: 12.5 }}>{p.sku || '—'}</td>
                                <td style={{ color: 'var(--ink-muted)', fontSize: 13 }}>{p.supplier_name}</td>
                                <td style={{ minWidth: 190 }}>{classificationControl(p)}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{formatCurrency(p.spend)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      )}

                      {/* Load-more rather than numbered pages: selection is carried across
                          loads and the bulk bar acts on it, so rows must only ever be
                          ADDED under the user. With pages, tagging a screenful and moving
                          on would silently drop the selection behind you. */}
                      {hasMore && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '18px 0 4px' }}>
                          <button className="btn btn-secondary" disabled={loadingMore}
                            onClick={() => loadProducts(products.length)}>
                            {loadingMore ? 'טוען…' : `טען עוד ${Math.min(PAGE_SIZE, total - products.length)} פריטים`}
                          </button>
                        </div>
                      )}
                      </>
                    )}
                  </section>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Once classifications exist the suggestion card demotes to one thin line —
          it is maintenance now, not the main event. */}
      {openSuggestions > 0 && canEdit && (
        <div className="card" style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13.5 }}>
            יש הצעות סיווג ל-<strong>{openSuggestions} ספקים</strong>
            {sugg?.suggested_spend > 0 && <> · {formatCurrency(sugg.suggested_spend)} מהרכש</>}
          </span>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowSuggestions(v => !v)}>
            {showSuggestions ? 'סגור' : 'סקור הצעות'}
          </button>
        </div>
      )}

      {showSuggestions && openSuggestions > 0 && canEdit && (
        <div className="card" style={{ marginBottom: 16 }}>
          <SuggestionTable
            sugg={sugg} picked={picked} setPicked={setPicked}
            pickedCount={pickedCount} busy={busy} onApply={applyPicked}
          />
        </div>
      )}

    </div>
  );
}
